import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  Badge,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import { Layer } from "../../types/layer";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter } from "./FilterModal";
import { getAttributesFromLayer } from "../../utils/layerFilters";
import { IncidentGroupVisibility } from "../layer_components/IncidentsLayer";
import { INCIDENT_STATUS_GROUPS } from "../../utils/incidentRekindle";

/**
 * One sidebar row covering the project's incidents and every realtime
 * detection layer.
 *
 * Hotspots, lightning and hazards incidents are the same idea —
 * externally-ingested live detections — and each was taking a top-level row
 * for a single eye toggle. They share a "Realtime" row whose eye drives them
 * all at once, with the per-kind toggles and filters in the hover panel. The
 * Incidents layer (FiSci's own tracked fires, drawn from those detections)
 * sits at the top of the same panel with its own options — history window
 * and status groups — so the sidebar has one live-data row, not two.
 */

const INCIDENT_DAY_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 0, label: "All history" },
];

type RealtimeControlKind =
  | "hotspot"
  | "lightning"
  | "hazard"
  | "ignition"
  | "burnt_area";

interface RealtimeSource {
  kind: RealtimeControlKind;
  label: string;
  icon: string;
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  /** False when the organisation has no data source for this kind, or is not
   *  entitled to it. Unavailable kinds are simply not listed — only FiSci
   *  staff can change that, so there is nothing here for a user to act on. */
  available: boolean;
}

interface RealtimeControlProps {
  hotspotsLayer: Layer | null;
  hotspotsFilters: Filter[];
  onHotspotsFiltersChange: (filters: Filter[]) => void;
  hotspotsAvailable?: boolean;
  lightningLayer: Layer | null;
  lightningFilters: Filter[];
  onLightningFiltersChange: (filters: Filter[]) => void;
  lightningAvailable?: boolean;
  hazardsLayer?: Layer | null;
  hazardsFilters?: Filter[];
  onHazardsFiltersChange?: (filters: Filter[]) => void;
  hazardsAvailable?: boolean;
  /** Customer ArcGIS kinds. "ignitionPoints", not "ignitions": the project
   *  map already has an ignitionsLayer (simulation ignitions). */
  ignitionPointsLayer?: Layer | null;
  ignitionPointsFilters?: Filter[];
  onIgnitionPointsFiltersChange?: (filters: Filter[]) => void;
  ignitionPointsAvailable?: boolean;
  burntAreasLayer?: Layer | null;
  burntAreasFilters?: Filter[];
  onBurntAreasFiltersChange?: (filters: Filter[]) => void;
  burntAreasAvailable?: boolean;
  /** Project map only: the Incidents layer and its options. */
  incidentsLayer?: (Layer & { incidentCount?: number }) | null;
  incidentDays?: number;
  onIncidentDaysChange?: (days: number) => void;
  incidentGroups?: IncidentGroupVisibility;
  onIncidentGroupsChange?: (groups: IncidentGroupVisibility) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** When provided, the panel offers "Simulate detection", which arms a
   *  click-to-place mode on the map (listing map only). */
  onStartSimulateDetection?: () => void;
  onCancelSimulateDetection?: () => void;
  simulatingDetection?: boolean;
}

const RealtimeControl: React.FC<RealtimeControlProps> = ({
  hotspotsLayer,
  hotspotsFilters,
  onHotspotsFiltersChange,
  hotspotsAvailable = true,
  lightningLayer,
  lightningFilters,
  onLightningFiltersChange,
  lightningAvailable = true,
  hazardsLayer = null,
  hazardsFilters = [],
  onHazardsFiltersChange,
  hazardsAvailable = true,
  ignitionPointsLayer = null,
  ignitionPointsFilters = [],
  onIgnitionPointsFiltersChange,
  ignitionPointsAvailable = true,
  burntAreasLayer = null,
  burntAreasFilters = [],
  onBurntAreasFiltersChange,
  burntAreasAvailable = true,
  incidentsLayer = null,
  incidentDays = 30,
  onIncidentDaysChange,
  incidentGroups,
  onIncidentGroupsChange,
  expanded,
  onExpandedChange,
  onStartSimulateDetection,
  onCancelSimulateDetection,
  simulatingDetection = false,
}) => {
  const [filterModalKind, setFilterModalKind] =
    useState<RealtimeControlKind | null>(null);
  const [incidentOptionsOpen, setIncidentOptionsOpen] = useState(false);
  // The history <Select> opens a portal menu; keep the hover panel up while
  // the pointer is in it.
  const [incidentMenuOpen, setIncidentMenuOpen] = useState(false);

  const sources: RealtimeSource[] = useMemo(
    () =>
      [
        {
          kind: "hotspot" as const,
          label: "Hotspots",
          icon: "local_fire_department",
          layer: hotspotsLayer,
          filters: hotspotsFilters,
          onFiltersChange: onHotspotsFiltersChange,
          available: hotspotsAvailable,
        },
        {
          kind: "lightning" as const,
          label: "Lightning",
          icon: "bolt",
          layer: lightningLayer,
          filters: lightningFilters,
          onFiltersChange: onLightningFiltersChange,
          available: lightningAvailable,
        },
        {
          kind: "hazard" as const,
          // The state agencies' hazard feed — not FiSci's own incidents,
          // which are a separate layer above.
          label: "Hazards incidents",
          icon: "warning",
          layer: hazardsLayer,
          filters: hazardsFilters,
          onFiltersChange: onHazardsFiltersChange ?? (() => {}),
          available: hazardsAvailable,
        },
        {
          kind: "ignition" as const,
          label: "Ignition points",
          icon: "whatshot",
          layer: ignitionPointsLayer,
          filters: ignitionPointsFilters,
          onFiltersChange: onIgnitionPointsFiltersChange ?? (() => {}),
          available: ignitionPointsAvailable,
        },
        {
          kind: "burnt_area" as const,
          label: "Burnt areas",
          icon: "landscape",
          layer: burntAreasLayer,
          filters: burntAreasFilters,
          onFiltersChange: onBurntAreasFiltersChange ?? (() => {}),
          available: burntAreasAvailable,
        },
      ].filter((s) => s.layer),
    [
      hotspotsLayer,
      hotspotsFilters,
      onHotspotsFiltersChange,
      hotspotsAvailable,
      lightningLayer,
      lightningFilters,
      onLightningFiltersChange,
      lightningAvailable,
      hazardsLayer,
      hazardsFilters,
      onHazardsFiltersChange,
      hazardsAvailable,
      ignitionPointsLayer,
      ignitionPointsFilters,
      onIgnitionPointsFiltersChange,
      ignitionPointsAvailable,
      burntAreasLayer,
      burntAreasFilters,
      onBurntAreasFiltersChange,
      burntAreasAvailable,
    ]
  );

  const attributesByKind = useMemo(() => {
    const map: Record<string, ReturnType<typeof getAttributesFromLayer>> = {};
    for (const s of sources) {
      map[s.kind] = s.layer?.rawSource
        ? getAttributesFromLayer(s.layer.rawSource)
        : [];
    }
    return map;
  }, [sources]);

  // Unavailable kinds are dropped outright rather than shown greyed out:
  // enabling one is a FiSci-staff action, so a disabled row would be an
  // affordance nobody in this UI can take. With none available the whole row
  // disappears.
  const viewable = sources.filter((s) => s.available);
  const incidentsVisible = Boolean(incidentsLayer?.active);
  if (!viewable.length && !incidentsLayer) return null;
  const anyVisible = viewable.some((s) => s.layer?.active) || incidentsVisible;
  const totalFilters = viewable.reduce((n, s) => n + s.filters.length, 0);
  const modalSource = sources.find((s) => s.kind === filterModalKind);

  const toggle = (source: RealtimeSource) => {
    if (!source.available) return;
    source.layer?.toolbarButton?.props?.onClick?.();
  };
  const toggleIncidents = () => {
    incidentsLayer?.toolbarButton?.props?.onClick?.();
  };

  // Group eye: turn everything off if anything is on, otherwise turn all on.
  // Toggling each layer individually would leave a mixed state that the single
  // group icon cannot represent.
  const handleToggleAll = () => {
    for (const source of viewable) {
      if (Boolean(source.layer?.active) === anyVisible) toggle(source);
    }
    if (incidentsLayer && incidentsVisible === anyVisible) toggleIncidents();
  };

  const eyeButton = (visible: boolean, label: string, onClick: () => void) => (
    <Tooltip title={visible ? `Hide ${label}` : `Show ${label}`}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            color: visible ? "#00d4ad" : "#64748b",
            padding: "2px",
            "&.Mui-disabled": { color: "#475569" },
          }}
          aria-label={`${visible ? "Hide" : "Show"} ${label}`}
        >
          {visible ? (
            <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: "0.875rem" }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  const sectionLabel = (text: string, mt = 0) => (
    <Typography
      sx={{
        color: "#94a3b8",
        fontWeight: 600,
        textTransform: "uppercase",
        fontSize: "0.6rem",
        letterSpacing: "0.04em",
        mt,
        mb: 0.25,
      }}
    >
      {text}
    </Typography>
  );

  const incidentsRow = incidentsLayer && incidentGroups && (
    <>
      {sectionLabel("Incidents")}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {eyeButton(incidentsVisible, "Incidents", toggleIncidents)}
        <Icon sx={{ fontSize: "0.95rem", color: "#94a3b8" }}>report</Icon>
        <Typography
          variant="caption"
          sx={{ color: "#e2e8f0", fontSize: "0.75rem", flex: 1 }}
        >
          Incidents
          {typeof incidentsLayer.incidentCount === "number"
            ? ` (${incidentsLayer.incidentCount})`
            : ""}
        </Typography>
        <Tooltip title="Incident options — history window and which statuses to draw">
          <IconButton
            size="small"
            onClick={() => setIncidentOptionsOpen((o) => !o)}
            sx={{
              color: incidentOptionsOpen ? "#00d4ad" : "#64748b",
              padding: "2px",
            }}
            aria-label="Incident options"
          >
            <TuneIcon sx={{ fontSize: "0.875rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Collapse in={incidentOptionsOpen}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            pl: 3,
            pr: 0.5,
            pb: 0.75,
          }}
        >
          <TextField
            select
            size="small"
            label="Closed history"
            value={incidentDays}
            onChange={(e) => onIncidentDaysChange?.(Number(e.target.value))}
            SelectProps={{
              onOpen: () => setIncidentMenuOpen(true),
              onClose: () => setIncidentMenuOpen(false),
            }}
            sx={{
              mt: 0.5,
              "& .MuiInputBase-root": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
          >
            {INCIDENT_DAY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.8rem" }}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          {INCIDENT_STATUS_GROUPS.map((group) => (
            <FormControlLabel
              key={group.key}
              control={
                <Checkbox
                  size="small"
                  checked={incidentGroups[group.key]}
                  onChange={(e) =>
                    onIncidentGroupsChange?.({
                      ...incidentGroups,
                      [group.key]: e.target.checked,
                    })
                  }
                  sx={{
                    py: 0,
                    color: "#94a3b8",
                    "&.Mui-checked": { color: "#00d4ad" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.75rem", color: "#e2e8f0" }}>
                  {group.label}
                </Typography>
              }
              sx={{ ml: 0, mr: 0 }}
            />
          ))}
          <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8" }}>
            Open incidents are always shown; the window trims closed history.
          </Typography>
        </Box>
      </Collapse>
      {viewable.length > 0 && sectionLabel("Detections", 0.75)}
    </>
  );

  const panelContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {incidentsRow}
      {onStartSimulateDetection && (
        <Button
          size="small"
          variant="outlined"
          onClick={
            simulatingDetection
              ? onCancelSimulateDetection
              : onStartSimulateDetection
          }
          sx={{
            mb: 0.5,
            color: simulatingDetection ? "#faad17" : "#00d4ad",
            borderColor: simulatingDetection ? "#faad17" : "#00d4ad",
            textTransform: "none",
            fontSize: "0.72rem",
            "&:hover": {
              backgroundColor: simulatingDetection
                ? "rgba(250, 173, 23, 0.1)"
                : "rgba(0, 212, 173, 0.1)",
            },
          }}
        >
          {simulatingDetection ? "Cancel" : "Simulate detection"}
        </Button>
      )}
      {viewable.map((source) => {
        const visible = Boolean(source.layer?.active);
        const attributes = attributesByKind[source.kind] ?? [];
        return (
          <Box
            key={source.kind}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            {eyeButton(visible, source.label, () => toggle(source))}

            <Icon sx={{ fontSize: "0.95rem", color: "#94a3b8" }}>
              {source.icon}
            </Icon>

            <Typography
              variant="caption"
              sx={{ color: "#e2e8f0", fontSize: "0.75rem", flex: 1 }}
            >
              {source.label}
            </Typography>

            <Tooltip
              title={
                attributes.length === 0
                  ? "No data loaded — add features to enable filters"
                  : `Filter ${source.label}`
              }
            >
              {/* span so the tooltip still shows while the button is disabled */}
              <span>
                <IconButton
                  size="small"
                  disabled={attributes.length === 0}
                  onClick={() => setFilterModalKind(source.kind)}
                  sx={{
                    color: source.filters.length ? "#00d4ad" : "#64748b",
                    padding: "2px",
                    "&.Mui-disabled": { color: "#475569" },
                  }}
                  aria-label={`Filter ${source.label}`}
                >
                  <Badge
                    badgeContent={source.filters.length}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.55rem",
                        height: 12,
                        minWidth: 12,
                      },
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: "0.875rem" }} />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <>
      {modalSource && (
        <FilterModal
          open
          onClose={() => setFilterModalKind(null)}
          title={modalSource.label}
          attributes={attributesByKind[modalSource.kind] ?? []}
          filters={modalSource.filters}
          onFiltersChange={modalSource.onFiltersChange}
        />
      )}

      <LayerControlItem
        title="Realtime"
        icon="satellite_alt"
        visible={anyVisible}
        onToggleVisibility={handleToggleAll}
        enabled={true}
        // Combined count on the collapsed icon badge, which does not depend on
        // onFilterClick. The row's own filter button is deliberately omitted:
        // filtering is per-kind, so a single button here would be ambiguous,
        // and passing it disabled would claim "No data to filter" — wrong when
        // the data is present and simply filtered from the rows below.
        filterCount={totalFilters}
        drawingContent={panelContent}
        drawingContentLabel={incidentsLayer ? "Incidents & Detections" : "Detection Layers"}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        // Holds the hover panel open while the filter modal is up. No
        // onOutsideClick: the modal is a portal, so every click inside it is
        // "outside" the panel — releasing state there would shut the modal the
        // moment the user touched it. It closes through its own onClose, which
        // drops keepOpen and lets the panel collapse.
        keepOpen={filterModalKind !== null || incidentMenuOpen}
      />
    </>
  );
};

export default RealtimeControl;
